import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { Sparkle, Truck, ShoppingCart, TShirt, Wrench } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'customer'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(formData);
      toast.success('Account created successfully!');
      
      if (user.role === 'worker') {
        navigate('/worker/dashboard');
      } else {
        navigate('/customer/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12 text-white relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-1/4 w-[400px] h-[400px] bg-[#D4AF37]/15 rounded-full blur-[100px]" />
          <div className="absolute bottom-20 right-1/4 w-[300px] h-[300px] bg-[#F4C2C2]/10 rounded-full blur-[80px]" />
        </div>
        
        <div className="relative z-10 text-center max-w-lg">
          {/* Logo */}
          <div className="mb-8">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] rounded-2xl flex items-center justify-center mb-6 shadow-2xl shadow-[#D4AF37]/20">
              <span className="text-black font-bold text-3xl">H</span>
            </div>
            <h1 className="text-5xl font-bold font-['Cormorant_Garamond'] tracking-tight">
              <span className="bg-gradient-to-r from-[#D4AF37] to-[#F4C2C2] bg-clip-text text-transparent">NC Help Hub</span>
            </h1>
            <p className="text-white/60 text-lg mt-2">Your Hub for Home Help</p>
          </div>
          
          {/* Service Icons */}
          <div className="grid grid-cols-5 gap-4 mb-10">
            <div className="text-center group">
              <div className="w-12 h-12 bg-[var(--background-paper)]/5 border border-white/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:border-[#D4AF37]/50 transition-colors">
                <Sparkle className="w-5 h-5 text-[#D4AF37]" weight="duotone" />
              </div>
              <p className="text-xs text-white/50">Cleaning</p>
            </div>
            <div className="text-center group">
              <div className="w-12 h-12 bg-[var(--background-paper)]/5 border border-white/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:border-[#F4C2C2]/50 transition-colors">
                <TShirt className="w-5 h-5 text-[#F4C2C2]" weight="duotone" />
              </div>
              <p className="text-xs text-white/50">Laundry</p>
            </div>
            <div className="text-center group">
              <div className="w-12 h-12 bg-[var(--background-paper)]/5 border border-white/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:border-[#D4AF37]/50 transition-colors">
                <Truck className="w-5 h-5 text-[#D4AF37]" weight="duotone" />
              </div>
              <p className="text-xs text-white/50">Courier</p>
            </div>
            <div className="text-center group">
              <div className="w-12 h-12 bg-[var(--background-paper)]/5 border border-white/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:border-[#F4C2C2]/50 transition-colors">
                <ShoppingCart className="w-5 h-5 text-[#F4C2C2]" weight="duotone" />
              </div>
              <p className="text-xs text-white/50">Errands</p>
            </div>
            <div className="text-center group">
              <div className="w-12 h-12 bg-[var(--background-paper)]/5 border border-white/10 rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:border-[#D4AF37]/50 transition-colors">
                <Wrench className="w-5 h-5 text-[#D4AF37]" weight="duotone" />
              </div>
              <p className="text-xs text-white/50">Tasks</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right Side - Register Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 bg-[#0A0A0A]">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md pb-24 lg:pb-0"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-14 h-14 mx-auto bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] rounded-xl flex items-center justify-center mb-3">
              <span className="text-black font-bold text-xl">H</span>
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F4C2C2] bg-clip-text text-transparent font-['Cormorant_Garamond']">
              NC Help Hub
            </h1>
          </div>
          
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white font-['Cormorant_Garamond']">Create Account</h2>
            <p className="text-white/50 mt-2">Join NC Help Hub and book services today</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="form-group">
              <Label htmlFor="full_name" className="text-white/80 font-medium">Full Name</Label>
              <div className="mt-2">
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="h-12 rounded-xl bg-[#111111] border border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] transition-colors w-full"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="email" className="text-white/80 font-medium">Email Address</Label>
              <div className="mt-2">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="h-12 rounded-xl bg-[#111111] border border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] transition-colors w-full"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="phone" className="text-white/80 font-medium">Phone Number</Label>
              <div className="mt-2">
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="h-12 rounded-xl bg-[#111111] border border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] transition-colors w-full"
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="password" className="text-white/80 font-medium">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  className="h-12 rounded-xl bg-[#111111] border border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] pr-12 transition-colors w-full"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#D4AF37] hover:bg-[#E6C965] text-black font-semibold rounded-xl transition-colors mt-2"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Creating account...
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  Create Account
                  <ArrowRight className="w-5 h-5" />
                </div>
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-white/50">
              Already have an account?{' '}
              <Link to="/login" className="text-[#D4AF37] hover:text-[#E6C965] font-medium transition-colors" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <p className="text-white/50">
              Just want to book quickly?{' '}
              <Link to="/services" className="text-[#F4C2C2] hover:text-[#F4C2C2]/80 font-medium transition-colors" data-testid="guest-checkout-link">
                Continue as Guest
              </Link>
            </p>
          </div>
          
          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <Link to="/careers">
              <Button 
                variant="ghost" 
                className="text-[#F4C2C2] hover:text-[#F4C2C2] hover:bg-[#F4C2C2]/10 rounded-xl"
                data-testid="careers-link"
              >
                Looking to work with us? View Careers
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;
